// src/pages/AccountSettings/index.tsx
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Alert, Box, Divider, Stack, Tab, Typography } from '@mui/material';
import React from 'react';

import { EditableText } from '@/components/EditableText';
import InitialLoader from '@/components/Loader/InitialLoader';

import {
  createStripeAccount,
  createStripeAccountLink,
  getStripeFeature,
  getUserInfo,
  updateUserInfo,
} from './api';
import {
  addUserAccount,
  deleteUser,
  listAccounts,
  resendUserAccountInvite,
  setMemberRole,
} from './api';
import { updateUserAccount } from './api';
import type {
  IAccountWithUserAccounts,
  IAddUserAccountRequest,
  IUserAccountBase,
  IUserData,
} from './types';
import { STRIPE_ACCOUNT_LINK_TYPE } from './types';
import { GeneralView } from './views/GeneralView';
import { TeamView } from './views/TeamView';
import { UserAlertsView } from './views/UserAlertsView';

export default function AccountSettings() {
  const [tab, setTab] = React.useState<'general' | 'team' | 'user'>('general');
  const [idUser, setIdUser] = React.useState<string>('');
  const [userData, setUserData] = React.useState<IUserData | null>(null);
  const [canConnectStripe, setCanConnectStripe] = React.useState(false);
  const [account, setAccount] = React.useState<IAccountWithUserAccounts | null>(null);
  const [subscriptionError, setSubscriptionError] = React.useState<string>('');

  // Function to refresh account data
  const refreshAccountData = React.useCallback(async () => {
    try {
      const accounts = await listAccounts();
      if (accounts.length) {
        setAccount(accounts[0]);
      }
    } catch (error) {
      console.error('Failed to refresh account data:', error);
    }
  }, []);

  React.useEffect(() => {
    // load the logged-in user + stripe flag
    getUserInfo().then((u) => {
      setIdUser(u.user.id);
    });
    getStripeFeature().then((f) => setCanConnectStripe(f.features.includes('BILLING')));
    // load account for team tab
    refreshAccountData();
  }, [refreshAccountData]);

  // Update userData when account data loads
  React.useEffect(() => {
    if (account) {
      setUserData({
        orgName: account.organization_name,
        connectedAccountId: account.stripe_connected_account_id,
        connectedAccountDetailsSubmitted: account.stripe_connected_account_details_submitted,
        serviceFeeBillingMethod: account.service_fee_billing_method ?? 0, // Default to 0 if undefined
      });
    }
  }, [account]);

  // Restores original logic:
  const handleUpdateOrgName = async (newOrg: string) => {
    await updateUserInfo({ organization_name: newOrg }, idUser);
    // Refresh account data to get updated organization name
    refreshAccountData();
  };

  const handleCreateStripeAccountLink = async (): Promise<string> => {
    const { url } = await createStripeAccountLink({
      link_type: STRIPE_ACCOUNT_LINK_TYPE.ACCOUNT_ONBOARDING,
    });
    return url || '';
  };

  const handleConnectStripe = async (): Promise<string> => {
    try {
      // step 1: try to create stripe account
      await createStripeAccount();
      // Refresh account data to get updated Stripe info
      refreshAccountData();
    } catch (error) {
      // if account already exists (422), refresh account data to get existing stripe info
      refreshAccountData();
    }
    // step 2: return the onboarding link
    return await handleCreateStripeAccountLink();
  };

  if (!userData || !account) return <InitialLoader />;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Settings
        </Typography>
      </Box>

      {subscriptionError && (
        <Alert severity="error" onClose={() => setSubscriptionError('')} sx={{ mb: 2 }}>
          {subscriptionError}
        </Alert>
      )}

      {/** Show organization name above tabs for all account types */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ mb: 2, display: 'flex' }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Organization Name:
          </Typography>
        </Box>
        {/*
          TODO: backend OpenAPI does not enforce this length. Add `maxLength: 64` to
          every `organization_name` schema in customer-lambdas/doc/webapp.yaml (mirroring
          users.organization_name varchar(64) in db_setup.sql), then drop this maxLength
          prop. Today the DB write fails with a cryptic string_data_right_truncation
          error if the cap is bypassed.
        */}
        <EditableText
          value={userData.orgName}
          onUpdate={handleUpdateOrgName}
          maxLength={64}
          required
          requiredErrorText="Organization name is required"
        />
      </Stack>

      <TabContext value={tab}>
        <TabList
          indicatorColor="primary"
          textColor="primary"
          onChange={(_e, v) => {
            const newTab = v as any;
            setTab(newTab);
            // Refresh account data when switching to team tab to ensure latest notification counts
            if (newTab === 'team') {
              refreshAccountData();
            }
          }}
        >
          <Tab label="General" value="general" />
          <Tab label="Team Members" value="team" />
          <Tab label="Notifications" value="notifications" />
        </TabList>
        <Divider sx={{ borderColor: 'grey.300' }} />

        <TabPanel value="general">
          <GeneralView
            userData={userData}
            onConnect={handleConnectStripe}
            onResumeSetup={handleCreateStripeAccountLink}
            canConnectStripe={canConnectStripe}
            canUseStripeServiceFees={userData?.serviceFeeBillingMethod === 1}
            onSubscriptionError={setSubscriptionError}
          />
        </TabPanel>

        <TabPanel value="team">
          <TeamView
            account={account}
            currentUserId={idUser}
            onInvite={async (body: IAddUserAccountRequest) => {
              const updated = await addUserAccount(body);
              setAccount(updated);
            }}
            onResend={async (uid, memo) => {
              await resendUserAccountInvite(uid, memo || '');
            }}
            onUpdate={async (
              user: IUserAccountBase,
              updates: { note?: string | null; phone_number?: string | null },
            ) => {
              const updatePayload: any = {
                enabled_email_notifications: user.enabled_email_notifications,
                enabled_sms_notifications: user.enabled_sms_notifications || [],
              };

              if (updates.note !== undefined) {
                updatePayload.note = updates.note || '';
              } else {
                updatePayload.note = user.note || '';
              }

              if (updates.phone_number !== undefined) {
                updatePayload.phone_number = updates.phone_number;
              }

              const updated = await updateUserAccount(user.id, updatePayload);
              setAccount((acct) => ({
                ...acct!,
                associated_user_accounts: acct!.associated_user_accounts.map((u) =>
                  u.id === user.id ? updated : u,
                ),
              }));
            }}
            onRoleChange={async (userId: string, newRoleKey: string) => {
              const updated = await setMemberRole(userId, newRoleKey);
              setAccount((acct) => ({
                ...acct!,
                associated_user_accounts: acct!.associated_user_accounts.map((u) =>
                  u.id === userId ? updated : u,
                ),
              }));
            }}
            onDelete={async (userId: string) => {
              const updated = await deleteUser(userId);
              setAccount(updated);
            }}
          />
        </TabPanel>
        <TabPanel value="notifications">
          <UserAlertsView
            account={account}
            currentUserId={idUser}
            onRefreshAccount={refreshAccountData}
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
}
